import { createContext, useContext, type ComponentPropsWithoutRef } from "react";
import {
  composeRenderProps,
  type Key,
  ToggleButton as AriaToggleButton,
  ToggleButtonGroup as AriaToggleButtonGroup
} from "react-aria-components";
import { cn, type VariantProps } from "tailwind-variants";
import { toggleVariants } from "./toggle";

export type ToggleGroupKey = Key;

const ToggleGroupContext = createContext<VariantProps<typeof toggleVariants>>({
  size: "default",
  variant: "default"
});

type ToggleGroupProps = ComponentPropsWithoutRef<typeof AriaToggleButtonGroup> &
  VariantProps<typeof toggleVariants>;

function ToggleGroup({ className, variant, size, ...props }: ToggleGroupProps) {
  return (
    <ToggleGroupContext.Provider value={{ variant, size }}>
      <AriaToggleButtonGroup
        data-slot="toggle-group"
        className={composeRenderProps(
          className,
          classNameValue => cn("flex items-center justify-center gap-1", classNameValue)!
        )}
        {...props}
      />
    </ToggleGroupContext.Provider>
  );
}

type ToggleGroupItemProps = Omit<ComponentPropsWithoutRef<typeof AriaToggleButton>, "className"> &
  VariantProps<typeof toggleVariants> & {
    className?: string;
  };

function ToggleGroupItem({ className, children, variant, size, ...props }: ToggleGroupItemProps) {
  const context = useContext(ToggleGroupContext);

  return (
    <AriaToggleButton
      data-slot="toggle-group-item"
      className={composeRenderProps(className, classNameValue =>
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
          className: classNameValue
        })
      )}
      {...props}
    >
      {children}
    </AriaToggleButton>
  );
}

export { ToggleGroup, ToggleGroupItem };
